import React, { useState, useEffect, useRef } from "react";

// components
import Navbar from "components/Navbars/AuthNavbar.js";
import filesService from "services/files.service";

const POLL_INTERVAL = 5000; // 5 seconds

export default function RedirectPage() {
    const [status, setStatus] = useState('loading'); // loading, processing, completed, error, no-id
    const [analysisId, setAnalysisId] = useState(null);
    const intervalRef = useRef(null);

    // Step 1: Read analysisId from URL on mount
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const id = params.get('analysisId');
        if (id) {
            setAnalysisId(id);
            setStatus('processing');
        } else {
            // No analysisId in URL — show fallback
            setStatus('no-id');
        }
    }, []);

    // Step 2: Poll for status when we have an analysisId
    useEffect(() => {
        if (!analysisId) return;

        const poll = async () => {
            try {
                console.log('[RedirectPage] Polling status for:', analysisId);
                const response = await filesService.getAnalysisStatus(analysisId);
                console.log('[RedirectPage] Status response:', response.data);
                if (response.status === 200) {
                    const currentStatus = response.data.status;
                    if (currentStatus === 'completed') {
                        console.log('[RedirectPage] Analysis completed!');
                        setStatus('completed');
                        if (intervalRef.current) clearInterval(intervalRef.current);
                    } else if (currentStatus === 'error') {
                        console.log('[RedirectPage] Analysis error!');
                        setStatus('error');
                        if (intervalRef.current) clearInterval(intervalRef.current);
                    }
                    // Otherwise keep polling (status is processing, paid, ready, etc.)
                }
            } catch (error) {
                console.error("[RedirectPage] Error polling status:", error);
                // Don't stop polling on transient errors
            }
        };

        // Poll immediately, then every POLL_INTERVAL
        poll();
        intervalRef.current = setInterval(poll, POLL_INTERVAL);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [analysisId]);

    const handleViewReport = () => {
        window.location.href = `/subasta/${analysisId}`;
    };

    const handleEmailRedirect = () => {
        window.location.href = 'mailto:';
    };

    // Don't render anything while determining initial state
    if (status === 'loading') {
        return (
            <>
                <Navbar transparent />
                <main>
                    <div className="relative pt-20 pb-6 flex content-center items-center justify-center min-h-screen h-full">
                        <div className="absolute top-0 w-full h-full bg-center bg-cover bg-blueGray-100 z-0"></div>
                    </div>
                </main>
            </>
        );
    }

    return (
        <>
            <Navbar transparent />
            <main>
                <div className="relative pt-20 pb-6 flex content-center items-center justify-center min-h-screen h-full">
                    <div
                        className="absolute top-0 w-full h-full bg-center bg-cover bg-blueGray-100 z-0"
                    >
                    </div>
                    <div className="flex flex-col container content-center items-center justify-center w-full h-full z-10 px-4">

                        {/* Report is ready */}
                        {status === 'completed' && (
                            <>
                                <div className="w-14 h-14 rounded-full bg-green-100 p-2 flex items-center justify-center mx-auto mb-5 shadow-sm">
                                    <i className="fas fa-check text-green-500 text-2xl"></i>
                                </div>
                                <p className="mb-2 text-2xl font-bold text-gray-900">
                                    ¡Tu informe está listo!
                                </p>
                                <p className="mb-6 text-lg text-center text-gray-600 max-w-md">
                                    El análisis de tu subasta se ha completado. Puedes ver el informe ahora mismo.
                                </p>
                                <button
                                    onClick={handleViewReport}
                                    className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-3 px-8 rounded-xl text-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                                >
                                    <i className="fas fa-file-lines mr-3"></i>
                                    Ver informe
                                </button>
                                <p className="mt-6 text-sm text-gray-500">
                                    También te hemos enviado el enlace por correo electrónico.
                                </p>
                            </>
                        )}

                        {/* Still processing */}
                        {status === 'processing' && (
                            <>
                                <div className="w-14 h-14 rounded-full bg-blue-100 p-2 flex items-center justify-center mx-auto mb-5 shadow-sm">
                                    <i className="fas fa-spinner fa-spin text-blue-500 text-2xl"></i>
                                </div>
                                <p className="mb-2 text-2xl font-bold text-gray-900">
                                    Estamos analizando tu subasta
                                </p>
                                <p className="mb-6 text-lg text-center text-gray-600 max-w-md">
                                    El informe suele tardar entre 1 y 2 minutos. Puedes esperar aquí o cerrar la página y consultar tu correo más tarde.
                                </p>

                                {/* Progress animation */}
                                <div className="w-full max-w-xs mb-8">
                                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-blue-500 rounded-full"
                                            style={{
                                                animation: 'progressPulse 2s ease-in-out infinite',
                                                width: '60%',
                                            }}
                                        ></div>
                                    </div>
                                </div>

                                <div className="flex flex-col items-center gap-3 mt-2">
                                    <p className="text-sm text-gray-500">
                                        <i className="fas fa-envelope mr-2"></i>
                                        Te enviaremos un correo cuando esté listo.
                                    </p>
                                    <button
                                        onClick={handleEmailRedirect}
                                        className="text-blue-600 hover:text-blue-700 font-medium text-sm underline underline-offset-2 transition-colors"
                                    >
                                        Abrir correo
                                    </button>
                                </div>
                            </>
                        )}

                        {/* Error */}
                        {status === 'error' && (
                            <>
                                <div className="w-14 h-14 rounded-full bg-red-100 p-2 flex items-center justify-center mx-auto mb-5 shadow-sm">
                                    <i className="fas fa-exclamation-triangle text-red-500 text-2xl"></i>
                                </div>
                                <p className="mb-2 text-2xl font-bold text-gray-900">
                                    Ha ocurrido un error
                                </p>
                                <p className="mb-6 text-lg text-center text-gray-600 max-w-md">
                                    No hemos podido completar el análisis. Te hemos devuelto el crédito. Por favor, inténtalo de nuevo más tarde o contacta con nosotros.
                                </p>
                                <button
                                    onClick={() => window.location.href = '/subasta'}
                                    className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-xl text-lg transition-all duration-200 shadow-md hover:shadow-lg"
                                >
                                    <i className="fas fa-arrow-left mr-3"></i>
                                    Volver a intentar
                                </button>
                            </>
                        )}

                        {/* No analysisId — fallback for old links */}
                        {status === 'no-id' && (
                            <>
                                <div className="w-14 h-14 rounded-full bg-green-100 p-2 flex items-center justify-center mx-auto mb-5 shadow-sm">
                                    <i className="fas fa-check text-green-500 text-2xl"></i>
                                </div>
                                <p className="mb-2 text-2xl font-bold text-gray-900">
                                    ¡Ya estamos analizando tu subasta!
                                </p>
                                <p className="mb-6 text-lg text-center text-gray-600 max-w-md">
                                    En cuanto el informe esté listo, te enviaremos un correo electrónico con los resultados.
                                </p>
                                <button
                                    onClick={handleEmailRedirect}
                                    className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2 px-4 rounded-lg mt-4"
                                >
                                    <i className="fas fa-envelope mr-3"></i>
                                    Abrir correo
                                </button>
                            </>
                        )}
                    </div>
                </div>

                <style>{`
                    @keyframes progressPulse {
                        0% { width: 15%; opacity: 0.7; }
                        50% { width: 85%; opacity: 1; }
                        100% { width: 15%; opacity: 0.7; }
                    }
                `}</style>
            </main>
        </>
    );
}